import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../modules/layout/components/ToastContext";
import { getAll, getById, post } from "../_request";
import { ILibraryWithClassNamesDto, IFilterLibraryModel, IAxiosGetResponse } from "../../interfaces/IUtilities";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import { Field, Form, Formik } from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import SortDirection from "../../modules/layout/components/SortDirection";
import Pagination from "../../modules/layout/components/Pagination";

const AddLibrariesToTransactionRoute = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const { serviceid, transactionid } = useParams();

    const [service, setService] = useState({} as string);
    const [transaction, setTransaction] = useState({} as string);
    const [libraries, setLibraries] = useState<ILibraryWithClassNamesDto[]>([]);
    const [selectedLibraryIds, setSelectedLibraryIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortField, setSortField] = useState("name");
    const [asc, setAsc] = useState(true);
    const [pageSize, setPageSize] = useState<number>(10);
    const order = asc ? "asc" : "desc";

    const initialFilters: IFilterLibraryModel = {
        name: "",
        libraryPath: "",
        serviceId: ""
    };

    const [activeFilters, setActiveFilters] = useState<IFilterLibraryModel>(initialFilters);

    const fetchServiceName = async () => {
        try {
            const result = (await getById("/api/services/", serviceid! + "/name")) as IAxiosGetResponse;
            if (result?.response.status === 200) {
                setService(result.data as string);
            } else {
                showToast(t("CouldNotFetchServiceName", { id: serviceid }), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        }
    };

    const fetchClassName = async () => {
        try {
            const result = (await getById("/api/transactionroutes/", transactionid + "/classname")) as IAxiosGetResponse;
            if (result?.response.status === 200) {
                setTransaction(result.data as string);
            } else {
                showToast(t("CouldNotFetchClassName", { id: transactionid }), "error");
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            showToast(t("ConnectionError", { error: String(error) }), "error");
        }
    };

    const fetchLibraries = async (filters: IFilterLibraryModel = {}) => {
        setLoading(true);
        try {
            if (!transactionid) {
                showToast(t("TransactionRouteIdMissing"), "error");
                navigate("/");
                return;
            }

            const params: { key: string; value: any }[] = [
                { key: "page", value: page },
                { key: "pageSize", value: pageSize },
                { key: "sortBy", value: sortField },
                { key: "order", value: order },
            ];

            if (filters.name) params.push({ key: "name", value: filters.name });
            if (filters.libraryPath) params.push({ key: "libraryPath", value: filters.libraryPath });
            if (filters.serviceId) params.push({ key: "serviceId", value: filters.serviceId });

            params.push({ key: "transactionId", value: transactionid });
            const queryString =
                "?" + params.map((f) => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value)}`).join("&");

            const result = await getAll(`/api/library/unlinked`, queryString);

            let respStatus = result?.status || result?.response.status;
            if (respStatus === 403) 
                navigate("/error/403");
            
            const items = result?.data?.items ?? [];
            setLibraries(items);

            const totalCount = result?.data?.totalCount ?? 0;
            const pages = Math.ceil(totalCount / pageSize);
            setTotalPages(pages > 0 ? pages : 1); // fix "Page 1 / 0"
            setTotal(result?.data.totalCount);
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServiceName();
        fetchClassName();
    }, []);

    useEffect(() => {
        fetchLibraries(activeFilters);
    }, [page, sortField, asc, pageSize]);

    const handleCheckboxChange = (id: number) => {
        setSelectedLibraryIds((prev) => (prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]));
    };

    const handleAddLibraries = async () => {
        try {
            const apiUrl = `/api/transactionroutes/${transactionid}/addlibraries`;
            const result = await post(apiUrl, selectedLibraryIds);
            if (result?.data) {
                showToast(t("LibrariesSuccessfullyAdded"), "success");
                navigate(`/services/${serviceid}/transactions/${transactionid}/libraries/list`);
            } else {
                showToast(t("GeneralError"), "error");
            }
        } catch (error) {
            showToast(t("GeneralError", { error: String(error) }), "error");
        }
    };

    const handleSort = (field: string) => {
        if (sortField === field) setAsc(!asc);
        else {
            setSortField(field);
            setAsc(true);
        }
    };

    const handleFilterSearch = async (filters: IFilterLibraryModel) => {
        setActiveFilters(filters);
        setPage(1);
        await fetchLibraries(filters);
    };

    return (
        <div className="container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t("Home"), href: "/" },
                    { title: t("Services"), href: "/services/list" },
                    { title: `${service}: ${t("TransactionRoutes")}`, href: `/services/${serviceid}/transactions/list` },
                    { title: `${transaction}: ${t("Libraries")}`, href: `/services/${serviceid}/transactions/${transactionid}/libraries/list` },
                    { title: t("AddLibraries"), href: "#" },
                ]}
            />

            <h3>{t("AddLibrariesToTransactionRoute")}</h3>

            <FiltersCard>
                <Formik initialValues={initialFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <div className="row g-3">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label">{t("Name")}</label>
                                    <Field className="form-control" name="name" />
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label">{t("LibraryPath")}</label>
                                    <Field className="form-control" name="libraryPath" />
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label">{t("ServiceId")}</label>
                                    <Field className="form-control" name="serviceId" />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-auto">
                                    <button type="submit" className="btn gg-btn-submit me-2">{t("Search")}</button>
                                    <button
                                        type="button"
                                        className="btn gg-btn"
                                        onClick={() => {
                                            resetForm();
                                            setActiveFilters(initialFilters);
                                            setPage(1);
                                            fetchLibraries(initialFilters);
                                        }}
                                    >
                                        {t("Reset")}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            {loading ? <Loader /> : (
                <>
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>{t("Select")}</th>
                                    <th onClick={() => handleSort("name")}>
                                        {t("Name")} <SortDirection isActive={sortField === "name"} order={order} />
                                    </th>
                                    <th onClick={() => handleSort("libraryPath")}>
                                        {t("LibraryPath")} <SortDirection isActive={sortField === "libraryPath"} order={order} />
                                    </th>
                                    <th >
                                        {t("ClassNames")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {libraries.length > 0 ? (
                                    libraries.map((lib) => (
                                        <tr key={lib.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedLibraryIds.includes(lib.id)}
                                                    onChange={() => handleCheckboxChange(lib.id)}
                                                />
                                            </td>
                                            <td>{lib.name}</td>
                                            <td>{lib.libraryPath}</td>
                                            <td>{lib.classNames}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3}>{t("NoLibrariesAvailable")}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination currentPage={page}
                        pageSize={pageSize}
                        onPageChange={(page) => { setPage(page) }}
                        onPageSizeChange={(size) => { setPageSize(size) }}
                        totalItems={total} />

                    {(() => {
                        const isAddDisabled = selectedLibraryIds.length === 0;
                        return (
                            <button
                                className={`btn ${isAddDisabled ? 'gg-btn' : 'gg-btn-submit'} mt-3`}
                                disabled={isAddDisabled}
                                onClick={handleAddLibraries}
                            >
                                {t("AddSelectedLibraries")}
                            </button>
                        );
                    })()}
                </>
            )}
        </div>
    );
};

export default AddLibrariesToTransactionRoute;
